import { createContext, useContext, useMemo } from "react";

const DEVELOPER_PORTAL_HOST_SUBSTRING = "developer-portal";

/**
 * Production host(s) that don't contain the "developer-portal" marker but
 * still serve the developer portal. Matched exactly so siblings like
 * `bayutsa-one.propforce.com` (the KSA staff portal) are NOT misclassified.
 */
const DEVELOPER_PORTAL_EXACT_HOSTS = new Set(["one.propforce.com"]);

/**
 * Detects developer portal from hostname (e.g. dev-zd-developer-portal.propone.pk
 * or the live one.propforce.com).
 * For local dev you must open the app using that host (not localhost), or set
 * REACT_APP_DEV_DEVELOPER_PORTAL=true — see isDeveloperPortalMode().
 */
export const isDeveloperPortalHost = (hostname = typeof window !== "undefined" ? window.location.hostname : "") =>
  hostname.includes(DEVELOPER_PORTAL_HOST_SUBSTRING) ||
  DEVELOPER_PORTAL_EXACT_HOSTS.has(hostname);

/**
 * Explicit opt-in for local development when hostname is still localhost.
 * Add to .env.local: REACT_APP_DEV_DEVELOPER_PORTAL=true
 * (Restart the dev server after changing env files.)
 */
export const isDeveloperPortalEnvOverride = () =>
  process.env.REACT_APP_DEV_DEVELOPER_PORTAL === "true";

/** True when the bundle should behave as the developer portal (production host or dev override). */
export const isDeveloperPortalMode = () =>
  isDeveloperPortalHost() || isDeveloperPortalEnvOverride();

const AppModeContext = createContext({
  portalMode: "staff",
});

export const AppModeProvider = ({ children }) => {
  const value = useMemo(() => {
    const portalMode = isDeveloperPortalMode() ? "developer" : "staff";
    return { portalMode };
  }, []);

  return <AppModeContext.Provider value={value}>{children}</AppModeContext.Provider>;
};

export const useAppMode = () => useContext(AppModeContext);
